import { useForm } from 'react-hook-form'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { FilterStore } from '../FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'components/UI/BaseDropdown/dropdown-base'
import { FilterTextInput } from '../inputs/FilterTextInput'
import { cn } from 'components/UI/cn'
import {
  FILTER_POPOVER_BODY,
  FILTER_POPOVER_CONTENT_BASE,
  FILTER_POPOVER_FOOTER,
} from './popoverClasses'
import { RadioInput } from 'components/FormUtils/RadioInput'
import FbButton from 'components/FbUI/FbButton'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'

interface ZipcodeFilterProps {
  filterStore: FilterStore
}

export function ZipcodeFilter({ filterStore }: ZipcodeFilterProps) {
  const { pendingZipCodeFilter, setPendingZipCodeFilter } = filterStore()
  // Register the literal "zipcode" key so the auto-count helper picks up
  // the zip filter (the store keys it specially; see useFilterGroupActiveCount).
  useRegisterFilterKey('zipcode')

  const [open, setOpen] = useState(false)
  const { control, reset, handleSubmit, watch } = useForm({
    defaultValues: {
      zipcode: pendingZipCodeFilter[0],
      mileRadius: pendingZipCodeFilter[1],
    },
    resolver: zodResolver(
      z.object({
        zipcode: z.string().min(5).max(5).regex(/^\d+$/),
        mileRadius: z.number().int().min(1).max(200),
      })
    ),
  })

  const clearFilters = () => {
    reset({
      zipcode: '',
      mileRadius: 0,
    })
    setPendingZipCodeFilter('', 0)
  }

  const _handleSubmit = handleSubmit((data) => {
    setPendingZipCodeFilter(data.zipcode, data.mileRadius)
    setOpen(false)
  })

  const isZipcodeSet =
    pendingZipCodeFilter[0] !== '' && pendingZipCodeFilter[1] !== 0

  return (
    <DropdownMenu open={open} onOpenChange={(open) => setOpen(open)}>
      <div className={'relative'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            isActive={isZipcodeSet}
            title={'ZIP code radius'}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isZipcodeSet}
          open={open}
          onClick={() => clearFilters()}
        />
      </div>
      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        // Mobile cap (FIR-7449): see popoverClasses.ts.
        className={cn(FILTER_POPOVER_CONTENT_BASE, 'w-[280px] lg:w-[260px]')}
        collisionPadding={12}
        sideOffset={6}
      >
        <div className={FILTER_POPOVER_BODY}>
          <FilterTextInput
            control={control}
            name={'zipcode'}
            maxLength={5}
            placeholder={'ZIP code'}
            noMargin
            className="mb-[8px]"
          />
          <div className="text-xs font-semibold text-neutral-500 px-1 pt-1 pb-1">
            Radius
          </div>
          <div className="ml-2">
            <RadioInput
              options={[
                { label: '1 mile', value: 1 },
                { label: '5 miles', value: 5 },
                { label: '10 miles', value: 10 },
                { label: '15 miles', value: 15 },
                { label: '25 miles', value: 25 },
                { label: '50 miles', value: 50 },
                { label: '100 miles', value: 100 },
                { label: '200 miles', value: 200 },
              ]}
              formControl={control}
              fieldName={'mileRadius'}
            />
          </div>
        </div>
        <div
          className={cn(
            FILTER_POPOVER_FOOTER,
            'mt-3 items-center justify-between gap-2'
          )}
        >
          <button
            type="button"
            onClick={() => clearFilters()}
            disabled={
              !isZipcodeSet && !watch('zipcode') && !watch('mileRadius')
            }
            className={cn(
              'text-sm font-medium transition-colors',
              !isZipcodeSet && !watch('zipcode') && !watch('mileRadius')
                ? 'text-neutral-400 cursor-not-allowed'
                : 'text-brand-active hover:text-brand-bright'
            )}
          >
            Reset
          </button>
          <div className="flex gap-2">
            <FbButton
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </FbButton>
            <FbButton
              size="sm"
              type="button"
              className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
              onClick={_handleSubmit}
              disabled={!watch('zipcode') || !watch('mileRadius')}
            >
              Apply
            </FbButton>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Tells FilterModal's alphabetical sort how to label this composite. The
// filter title is hardcoded inside via FilterButton, so the modal can't read
// it off the JSX.
ZipcodeFilter.sortMeta = { title: 'Zip Code' }
